import { isDefined } from '../../../../../../utils/variables';
import { DesignNode, RevertChange } from '../../../../../elements/design/design-node/design-node';
import { LineSegmentId } from '../../../../../elements/design/design-node/utils/line-segment-node';
import { PointId } from '../../../../../elements/design/design-node/utils/point-node';
import { combinePoints } from './point';
import { SelectionResult } from './select';

/**
 * All selected objects are deleted with the following criteria and order.
 * 1. Deletes all selected triangles.
 * 2. Deletes all selected lines, unless they are still connected to any triangles, and the seams
 *    of circles that are left without lines.
 * 3. Deletes all selected points.
 */
export function deleteSelection(
  designNode: DesignNode,
  selection: SelectionResult,
): RevertChange[] {
  const changes: RevertChange[] = [];
  const excludedPointIdMap = new Map<PointId, boolean>();

  // Remove faces.
  selection.faces.forEach((x) => {
    const face = designNode.getFaceById(x);
    excludedPointIdMap.set(face.values[0], true);
    excludedPointIdMap.set(face.values[1], true);
    excludedPointIdMap.set(face.values[2], true);

    changes.unshift(designNode.removeFace(x));
  });

  selection.lineSegments.forEach((x) => {
    const lineSegment = designNode.getLineSegmentById(x);

    excludedPointIdMap.set(lineSegment.values[0], true);
    excludedPointIdMap.set(lineSegment.values[1], true);

    changes.unshift(designNode.removeLineSegment(x));
  });

  // The seams only join the arcs of circles, see circle.ts. Selected points are removed below.
  const selectedPoints = new Set(selection.points);

  for (const id of excludedPointIdMap.keys()) {
    const point = designNode.getPointById(id);

    if (
      isDefined(point.seamOf) &&
      point.segments.length === 0 &&
      point.faces.length === 0 &&
      !selectedPoints.has(id)
    ) {
      changes.unshift(designNode.removePoint(id));
    }
  }

  /**
   * Remove points.
   *
   * Only delete the point if it is not connected to any deleted faces or lines unless
   * all of the surrounding geometry was deleted and its a loose hanging point.
   */
  for (const id of selection.points) {
    const pointToRemove = designNode.getPointById(id);

    if (pointToRemove.segments.length > 0) {
      if (excludedPointIdMap.has(id)) {
        continue;
      }

      const firstSegmentId = Math.min(...pointToRemove.segments) as LineSegmentId;

      const segment = designNode.getLineSegmentById(firstSegmentId);

      changes.unshift(
        ...combinePoints(
          designNode,
          segment.values[0] === pointToRemove.id ? segment.values[1] : segment.values[0],
          pointToRemove.id,
        ),
      );
    } else {
      changes.unshift(designNode.removePoint(pointToRemove.id));
    }
  }

  return changes;
}
